// L'affichage des cellules de l'ABS : un nombre au format français avec les décimales du contrat et
// son unité, sinon le texte, sinon « N/D » — jamais 0 — accompagné de son motif. Et les notes d'un
// tableau : les motifs d'absence numérotés, les sources avec leurs millésimes, les chiffres
// modifiés à la main (valeur d'origine, auteur, date, justification).
import type { BlocTableau, Cellule, Document, Modification } from './types'

export const ND = 'N/D'
const ESPACE_INSECABLE = ' '

export function formaterNombre(v: number, decimales = 0): string {
  return v.toLocaleString('fr-FR', { minimumFractionDigits: decimales, maximumFractionDigits: decimales })
}

export function estND(c: Cellule): boolean {
  return (c.valeur === null || c.valeur === undefined) && (c.texte === null || c.texte === undefined)
}

/** Une valeur (nombre ou texte) hors de toute cellule : la valeur d'origine d'une modification. */
export function formaterValeur(valeur: number | null | undefined, texte: string | null | undefined,
                               modele: Pick<Cellule, 'unite' | 'decimales'>): string {
  if (valeur !== null && valeur !== undefined) {
    const n = formaterNombre(valeur, modele.decimales ?? 0)
    return modele.unite ? `${n}${ESPACE_INSECABLE}${modele.unite}` : n
  }
  if (texte !== null && texte !== undefined) return texte
  return ND
}

export function formaterCellule(c: Cellule): string {
  return formaterValeur(c.valeur, c.texte, c)
}


/** Une date ISO en français (« 14 octobre 2026 ») ; telle quelle si elle ne se lit pas. */
export function dateLongue(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
}

export function dateHeure(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric',
                                     hour: '2-digit', minute: '2-digit' })
}

/** Une saisie au format français (« 19 200,5 », « −1,2 », « 22 % ») ; null si ce n'est pas un nombre. */
export function lireNombre(saisie: string): number | null {
  const t = saisie.trim()
    .replace(/[\s  ]/g, '')
    .replace(/[%€‰]$/u, '')
    .replace(/^[−–]/u, '-')
    .replace(',', '.')
  if (!/^-?\d+(\.\d+)?$/.test(t)) return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

/** La source d'une cellule, lisible : « Filosofi (Insee) ». */
export function libelleSource(doc: Pick<Document, 'sources'>, cle: string | null | undefined): string | null {
  if (!cle) return null
  const s = doc.sources[cle]
  if (!s) return cle
  return s.producteur && s.producteur !== s.libelle ? `${s.libelle} (${s.producteur})` : s.libelle
}

export interface NoteModification {
  ligne: string
  colonne: string
  /** l'adresse de la cellule (contrat : bloc, ligne, colonne) */
  adresse: { bloc: string; cle: string; champ: string }
  cellule: Cellule
  modification: Modification
}

export interface NotesTableau {
  /** les motifs d'absence distincts, dans l'ordre de lecture ; renvoi n = motifs[n - 1] */
  motifs: string[]
  renvoi: (c: Cellule) => number | null
  /** « Filosofi (Insee), 2021 » : chaque source citée par le tableau, avec ses millésimes */
  sources: string[]
  modifications: NoteModification[]
}

export function notesTableau(doc: Document, b: BlocTableau): NotesTableau {
  const motifs: string[] = []
  const parSource = new Map<string, string[]>()
  const modifications: NoteModification[] = []
  for (const ligne of b.lignes) {
    for (const col of b.colonnes) {
      const c = ligne.cellules[col.cle]
      if (!c) continue
      if (estND(c) && c.nd_motif && !motifs.includes(c.nd_motif.trim())) motifs.push(c.nd_motif.trim())
      if (c.source) {
        const m = parSource.get(c.source) ?? []
        const millesime = c.millesime ?? doc.sources[c.source]?.millesime
        if (millesime && !m.includes(millesime)) m.push(millesime)
        parSource.set(c.source, m)
      }
      if (c.modification) modifications.push({ ligne: ligne.libelle, colonne: col.libelle, cellule: c,
                                               adresse: { bloc: b.id, cle: ligne.id, champ: col.cle },
                                               modification: c.modification })
    }
  }
  const sources = [...parSource].map(([cle, millesimes]) => {
    const nom = libelleSource(doc, cle) ?? cle
    return millesimes.length ? `${nom}, ${millesimes.join(', ')}` : nom
  })
  const renvoi = (c: Cellule) => {
    if (!estND(c) || !c.nd_motif) return null
    const i = motifs.indexOf(c.nd_motif.trim())
    return i < 0 ? null : i + 1
  }
  return { motifs, renvoi, sources, modifications }
}
